import {AnyAction} from 'redux';

import {SETTINGSTAGS} from 'app/settings/api';
import {captureTokenAuthError, sweetAPI} from 'app/store/sweet-api';
import {showLoadingBar, showMessages} from 'app/store/sweet-api/utils';
import {
  EMAIL_API_URL,
  getEmailResendConfirmationAPIURL,
  LOGOUT_URL,
  RESET_URL,
  SCOPEDTOKENS_API_URL,
  TOKENS_API_URL,
  TOUR_API_URL,
  USERDATA_API_URL,
  USERDATA_SOCIAL_AUTH_API_URL,
  USERDATA_SUDO_API_URL,
} from 'app/urls';
import {gt} from 'app/utils/i18n';
import {hideLoadingAnimation} from 'app/utils/loading-animation';

import type {SocialAuthType} from './types';
import type {
  AuthenticatedUserSerializedType,
  LegacyTokenSerializedType,
  ListResponseType,
  ReactionSkinNameType,
  TokenSerializedType,
  UserAvailableTours,
  UserTourStatusType,
} from 'sweet-types';

export const CURRENT_USER_TAGS = {
  TOKENS: 'TOKENS',
  SCOPED: 'SCOPED',
  USER: 'USER',
};

const extendedAPI = sweetAPI
  .enhanceEndpoints({
    addTagTypes: [CURRENT_USER_TAGS.TOKENS, CURRENT_USER_TAGS.USER],
  })
  .injectEndpoints({
    endpoints: (build) => ({
      // get current user data
      getCurrentUser: build.query<AuthenticatedUserSerializedType, void>({
        query: () => USERDATA_API_URL,
        providesTags: () => [{type: CURRENT_USER_TAGS.USER, id: 'current'}],
        onQueryStarted: async (args, {queryFulfilled, ...api}) => {
          queryFulfilled.catch((result) => {
            captureTokenAuthError(result.error as any, api, [
              'required-not-in-scope',
            ]);
          });
        },
      }),

      // update current user navigation links
      updateCurrentUser: build.mutation<
        AuthenticatedUserSerializedType,
        {
          navigation_links?: string;
          emoji_skin_tone?: ReactionSkinNameType | '';
          timezone?: string;
          language?: string;
          avatar_image?: string | null;
        }
      >({
        query: (body) => ({url: USERDATA_API_URL, method: 'PATCH', body}),
        invalidatesTags: (result, error, {timezone, language}) => {
          const tags: Array<{type: string; id: string}> = [
            {type: CURRENT_USER_TAGS.USER, id: 'current'},
          ];
          if (timezone) {
            // refetch timezones if that changed.
            tags.push({type: SETTINGSTAGS.TIMEZONES, id: 'list'});
          }

          if (language) {
            // refetch languages if those changed.
            tags.push({type: SETTINGSTAGS.LANGUAGES, id: 'list'});
          }
          return tags;
        },
        onQueryStarted: async (args, {queryFulfilled, ...api}) => {
          showMessages({
            onSuccess: () => gt('Profile updated.'),
            onError: () =>
              gt("Couldn't update the your profile at the moment."),
          })(args, {queryFulfilled, ...api});

          queryFulfilled.then(({data}) => {
            api.dispatch(
              extendedAPI.util.updateQueryData(
                'getCurrentUser',
                undefined,
                () => data
              )
            );
          });
        },
      }),

      // update current-user tour status
      updateUserTourStatus: build.mutation<
        {status: UserTourStatusType},
        | {
            tourname: UserAvailableTours;
            tourversion: number;
            status: UserTourStatusType;
          }
        | {tourname: '*'; status: 1 | 0}
      >({
        query: (payload) => {
          const formData = new FormData();
          formData.append('action', 'update');

          for (const field of Object.keys(payload)) {
            formData.append(field, payload[field]);
          }

          return {
            url: TOUR_API_URL,
            method: 'POST',
            body: formData,
          };
        },
        invalidatesTags: () => [{type: CURRENT_USER_TAGS.USER, id: 'current'}],
        onQueryStarted: async ({tourname}, {queryFulfilled, ...api}) => {
          showMessages({
            onSuccess: () =>
              tourname === '*' ? gt('Tour status updated.') : null,
            onError: () => gt("Couldn't update the tour status at the moment."),
          })({tourname}, {queryFulfilled, ...api});
          queryFulfilled.then(({data}) => {
            // after updating the user tour status, update cached user data
            api.dispatch(
              extendedAPI.util.updateQueryData(
                'getCurrentUser',
                undefined,
                (currentUser) => {
                  // new users has no tour info stored. e.g. "tours" is an empty object
                  let tourStatus = {
                    ...(currentUser.tours[tourname] || {}),
                    status: data.status,
                  };

                  currentUser.tours[tourname] = tourStatus;
                }
              )
            );
          });
        },
      }),

      requestLogout: build.mutation<void, void>({
        query: () => ({
          url: LOGOUT_URL,
          method: 'POST',
          body: {},
          responseHandler: 'text',
        }),
        onQueryStarted: async (_, api) => {
          showMessages({onError: () => gt("Couldn't log out at the moment")})(
            _,
            api
          );

          let stopAnimation = false;
          window.SweetprocessLoadingScreen(() => stopAnimation);

          api.queryFulfilled.then(
            () => {
              // Redirect is made automatically via browser, and there is
              // no way of disabling that, so we need to hardcode it for now
              window.location.replace(RESET_URL);
            },
            () => {
              // whoops something went wrong while logging out
              // let's hide the loading-animation and show an error message
              stopAnimation = true;
              hideLoadingAnimation();
            }
          );
        },
      }),

      tokens: build.query<ListResponseType<TokenSerializedType>, void>({
        query: () => SCOPEDTOKENS_API_URL,
        providesTags: (response) =>
          response != null
            ? [{type: CURRENT_USER_TAGS.SCOPED, id: 'list'}]
            : [],
        onQueryStarted: (args, api) => {
          showLoadingBar(args, api);
          showMessages({
            onError: () => gt('Unable to load tokens.'),
          })(args, api);
        },
      }),

      deleteToken: build.mutation<unknown, {url: string}>({
        query: ({url}) => ({
          url,
          method: 'DELETE',
        }),
        onQueryStarted: showMessages({
          onSuccess: () => gt('Removed token'),
          onError: () => gt('Unable to remove token at the moment'),
        }),
        invalidatesTags: () => [{type: CURRENT_USER_TAGS.SCOPED, id: 'list'}],
      }),

      createToken: build.mutation<
        TokenSerializedType,
        {expires_at: string; scope: Array<string>; purpose: string}
      >({
        query: (body) => ({
          url: SCOPEDTOKENS_API_URL,
          method: 'POST',
          body,
        }),
        onQueryStarted: showMessages({
          onSuccess: () => gt('Token created'),
          onError: () => gt('Unable to create token'),
        }),
        invalidatesTags: () => [{type: CURRENT_USER_TAGS.SCOPED, id: 'list'}],
      }),

      updateToken: build.mutation<
        TokenSerializedType,
        {
          url: string;
          expires_at?: string;
          scope?: Array<string>;
          purpose?: string;
        }
      >({
        query: (body) => ({
          url: body.url,
          method: 'PATCH',
          body,
        }),
        onQueryStarted: showMessages({
          onSuccess: () => gt('Token updated'),
          onError: () => gt('Unable to update token'),
        }),
        invalidatesTags: () => [{type: CURRENT_USER_TAGS.SCOPED, id: 'list'}],
      }),

      legacyTokens: build.query<
        ListResponseType<LegacyTokenSerializedType>,
        void
      >({
        query: () => TOKENS_API_URL,
        providesTags: (response) =>
          response != null
            ? [{type: CURRENT_USER_TAGS.TOKENS, id: 'list'}]
            : [],
        onQueryStarted: (args, api) => {
          showLoadingBar(args, api);
          showMessages({
            onError: () => gt('Unable to load tokens.'),
          })(args, api);
        },
      }),

      deleteLegacyToken: build.mutation<unknown, {url: string}>({
        query: ({url}) => ({
          url,
          method: 'DELETE',
        }),
        onQueryStarted: showMessages({
          onSuccess: () => gt('Removed token'),
          onError: () => gt('Unable to remove token at the moment'),
        }),
        invalidatesTags: () => [{type: CURRENT_USER_TAGS.TOKENS, id: 'list'}],
      }),

      createLegacyToken: build.mutation<LegacyTokenSerializedType, void>({
        query: () => ({
          url: TOKENS_API_URL,
          method: 'POST',
        }),
        onQueryStarted: showMessages({
          onSuccess: () => gt('Token created'),
          onError: () => gt('Unable to create token'),
        }),
        invalidatesTags: () => [{type: CURRENT_USER_TAGS.TOKENS, id: 'list'}],
      }),

      email: build.query<
        ListResponseType<AuthenticatedUserSerializedType['emailaddresses'][0]>,
        void
      >({
        query: () => EMAIL_API_URL,
      }),

      deleteEmail: build.mutation<unknown, {url: string}>({
        query: ({url}) => ({url, method: 'DELETE'}),
        onQueryStarted: showMessages({
          onSuccess: () => gt('Removed email'),
          onError: () => gt('Unable to remove email at the moment'),
        }),

        invalidatesTags: () => [{type: CURRENT_USER_TAGS.USER, id: 'current'}],
      }),

      resendConfirmationEmail: build.mutation<unknown, {id: number}>({
        query: ({id}) => ({
          url: getEmailResendConfirmationAPIURL(id),
          method: 'POST',
          body: {},
        }),
        onQueryStarted: showMessages({
          onSuccess: () =>
            gt('Confirmation email queued for sending. Check your inbox.'),
          onError: () =>
            gt('Unable to send the confirmation email at the moment'),
        }),
        invalidatesTags: () => [{type: CURRENT_USER_TAGS.USER, id: 'current'}],
      }),

      sudoMode: build.mutation<unknown, {password: string}>({
        query: (body) => ({
          url: USERDATA_SUDO_API_URL,
          method: 'POST',
          body,
        }),
        onQueryStarted: showMessages({
          onSuccess: () =>
            gt('You have elevated permissions for the next few hours.'),
        }),
      }),

      socialAuth: build.query<Array<SocialAuthType>, undefined>({
        query: () => ({
          url: USERDATA_SOCIAL_AUTH_API_URL,
        }),
      }),
    }),
  });

/**** Current user ****/

// subscribe/fetch - to be used by class components
export const subscribeToCurrentUser =
  extendedAPI.endpoints.getCurrentUser.initiate;

// read cached data - to be used by useSelector / class components
export const CurrentUserSelector =
  extendedAPI.endpoints.getCurrentUser.select();

// https://redux-toolkit.js.org/rtk-query/usage/queries#hook-types
export const {
  useQuery: useCurrentUser,
  useLazyQuery: useLazyCurrentUser,
  useQueryState: useCurrentUserState,
} = extendedAPI.endpoints.getCurrentUser;

export const {
  useTokensQuery: useTokens,
  useCreateTokenMutation: useCreateToken,
  useDeleteTokenMutation: useDeleteToken,
  useUpdateTokenMutation: useUpdateToken,
  useLegacyTokensQuery: useLegacyTokens,
  useCreateLegacyTokenMutation: useCreateLegacyToken,
  useDeleteLegacyTokenMutation: useDeleteLegacyToken,
  useEmailQuery: useEmails,
  useDeleteEmailMutation: useDeleteEmail,
  useResendConfirmationEmailMutation: useResendConfirmationEmail,
  useSudoModeMutation: useSudoMode,
  useSocialAuthQuery: useCurrentUserSocialAuth,
} = extendedAPI;

// action to update current user
export const updateCurrentUser =
  extendedAPI.endpoints.updateCurrentUser.initiate;

// hook to update current user
export const {useMutation: useUpdateCurrentUser} =
  extendedAPI.endpoints.updateCurrentUser;

// return true when the action has current-user data
// it's intended to be used by other reducers which reads user data
export const hasCurrentUserData = (action: AnyAction): boolean => {
  return (
    extendedAPI.endpoints.getCurrentUser.matchFulfilled(action) ||
    extendedAPI.endpoints.updateCurrentUser.matchFulfilled(action)
  );
};

/**** User Tour ****/

// action to update  user tour status
export const {useMutation: useUpdateUserTourStatus} =
  extendedAPI.endpoints.updateUserTourStatus;

/**** user logout ****/

export const {useMutation: useLogout} = extendedAPI.endpoints.requestLogout;

export const requestLogout = extendedAPI.endpoints.requestLogout.initiate;
